import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { LoadingSkeletonComponent } from '@components';
import { getCustomerFacingActivityName, LeaderboardRange, UserRoleUid } from '@constants';
import { AuthedDirective, UnauthedDirective, UserRolesDirective } from '@directives';
import { GetInitialsPipe } from '@pipes';
import { AuthService, ContentService, KeplerSlapfiveService, UserService } from '@services';
import { CmsSlapFiveActivity } from '@shared/services/content/interfaces/cms-slapfive-activities.interface';
import { LeaderboardUser, SlapfiveUser } from '@types';
import { firstLastNameWesternFormat, getInitialsColors, parseActivityNameFromNotes, stripTags } from '@utils';
import dayjs from 'dayjs';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { GknRoutePath } from 'src/app/app-routing.enum';
import { LeaderboardService } from './leaderboard.service';

@Component({
    selector: 'app-leaderboard',
    templateUrl: './leaderboard.component.html',
    styleUrls: ['./leaderboard.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        LoadingSkeletonComponent,
        MatIconModule,
        MatTabsModule,
        MatTooltipModule,
        GetInitialsPipe,
        AuthedDirective,
        UnauthedDirective,
        UserRolesDirective,
        RouterLink,
    ],
})
export class LeaderBoardComponent implements OnInit, OnChanges {
    private authService = inject(AuthService);
    private leaderboardService = inject(LeaderboardService);
    private contentService = inject(ContentService);
    private keplerSlapfiveService = inject(KeplerSlapfiveService);
    private userService = inject(UserService);

    /** number of users to show */
    @Input() length: number;
    /** list of users to show on a given leaderboard range from {@link tab$} */
    users$: Observable<LeaderboardUser[]>;
    /** triggers an update to {@link users$} with either local cache or an API response */
    tab$ = new BehaviorSubject<LeaderboardRange>(LeaderboardService.DEFAULT_RANGE);
    /** enum access in html template */
    UserRoleUid = UserRoleUid;
    /** enum access in html template */
    LeaderboardRange = LeaderboardRange;
    /** Array constructor for template use */
    Array = Array;
    /** user details to display on the stats tab in leaderboard */
    user$: Observable<SlapfiveUser>;
    /** last activity display name resolved from CMS */
    lastActivityName$: Observable<string>;
    /** user profile picture URL */
    pfpLink$: Observable<string>;
    /** input search model */
    orchestrator: string = '';
    /** indicates whether a search query is actively loading */
    isSearching = false;
    /** Sign up route */
    signUpRoute = GknRoutePath.SIGNUP;
    profileRoutePath = '/' + GknRoutePath.PROFILE;
    /** Initials colors */
    initialsColors = new Map<string, { backgroundColor: string; textColor: string }>();

    /**
     * String identifiers for analytics services to track this tile, joined with hyphen in HTML
     */
    dataAnalytics: string[];
    slapfiveActivities$: Observable<CmsSlapFiveActivity[]>;

    ngOnInit(): void {
        this.fetchUsers();
        this.user$ = this.keplerSlapfiveService.user$;
        this.slapfiveActivities$ = this.contentService.slapfiveActivitiesPublic$;
        this.pfpLink$ = this.userService.pfpLink$;
        this.lastActivityName$ = this.keplerSlapfiveService.user$.pipe(
            switchMap((user) => this.getLastActivityName(user)),
        );
    }

    /**
     * Fetches users details from the leaderboard service
     */
    fetchUsers(): void {
        this.users$ = this.tab$.asObservable().pipe(
            switchMap((range) =>
                this.leaderboardService.getLeaderboard(range, this.length).pipe(map((users) => ({ range, users }))),
            ),
            map(({ range, users }) => {
                console.debug(`leaderboard::loaded ${range}`, users);
                return users;
            }),
            catchError((err) => {
                console.error('Failed to load leaderboard', err);
                return [];
            }),
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['length']) this.length = changes['length'].currentValue;
    }

    /**
     * Handles user clicks between tabs. Primary function is to emit a {@link tab$}
     * @param index - tab index
     * @public
     * @throws {Error} for deprecated leaderboard ranges
     */
    onTabChange(index: number): void {
        let tab: LeaderboardRange;
        if (index === 0) tab = LeaderboardRange.MY_STATS;
        if (index === 1) tab = LeaderboardRange.REALTIME;
        if (index === 2) tab = LeaderboardRange.ALL_TIME;
        if (index === 3) throw new Error('Unhandled range');

        if (this.tab$.value === tab) return; // prevent duplicate emissions due to mat-tab-group

        if (index === 1 || index === 2) {
            this.clearSearch();
        }

        console.debug(`leaderboard:: tab changed to ${tab}`);
        this.tab$.next(tab);
    }

    /**
     * Performs search for orchestrators based on the current search term
     */
    performSearch(): void {
        this.isSearching = true;

        const searchValue = this.orchestrator.trim();
        if (!searchValue) {
            this.clearSearch();
            return;
        }
        this.users$ = this.tab$.asObservable().pipe(
            switchMap((range) =>
                this.leaderboardService
                    .getLeaderboard(range, this.length, searchValue)
                    .pipe(map((users) => ({ range, users }))),
            ),
            tap(() => {
                this.isSearching = false;
            }),
            map(({ range, users }) => {
                console.debug(`leaderboard::searched ${range} with query "${searchValue}"`, users);
                return users;
            }),
            catchError((err) => {
                console.error('Failed to search leaderboard', err);
                this.isSearching = false;
                return of([]);
            }),
        );
    }

    /**
     * Resets the search term in daily and all time tab on leaderboard
     */
    clearSearch(): void {
        this.orchestrator = '';
        this.fetchUsers();
    }

    /**
     * Formats name to "FirstName LastInitial".
     * Since the names come from Okta, they're likely already
     * formatted in Western convention (First Last) regardless
     * of the user's cultural background. The function sticks to Western
     * naming conventions while excluding {@link LangNameParticle}.
     * @param name - name of the Orchestrator
     * @returns formatted name
     */
    formatUserName(name: string): string {
        const names = firstLastNameWesternFormat(name);
        return (names?.firstName + ' ' + (names?.lastInitial ? names.lastInitial + '.' : '')).trim();
    }

    /**
     * Returns a user-friendly name for the most recent activity log entry.
     * Prefers the WordPress card title parsed from the notes field;
     * falls back to the SlapFive activity type name.
     */
    getLastActivityName(user: SlapfiveUser): Observable<string> {
        const log = user?.activityLogs?.[0];
        const parsedName = parseActivityNameFromNotes(log?.notes);
        return this.contentService.slapfiveActivitiesPublic$.pipe(
            map((activities) =>
                activities.find((a) => {
                    const decoded = new DOMParser().parseFromString(stripTags(a.title.rendered), 'text/html').body
                        .textContent;
                    return decoded === parsedName;
                }),
            ),
            tap((activity) => console.log('featured activity', activity?.acf?.card?.body?.title)),
            map(
                (activity) =>
                    activity?.acf?.card?.body?.title ?? getCustomerFacingActivityName(log?.activityType?.name) ?? '',
            ),
        );
    }

    formatLastActivityDate(dateStr: string): string {
        const date = dayjs(dateStr);
        if (date.isValid()) return date.format('MMMM DD[,] YYYY');

        console.warn(
            `Parsed invalid date "${dateStr}" for Leaderboard > My Stats > Last activity, returning empty string.`,
        );
        return '';
    }

    /**
     * Sign in with Okta redirect
     * @param event - browser event
     */
    signInWithRedirect(e: Event): void {
        e?.stopPropagation();
        this.authService.signInWithRedirect();
    }

    /**
     * Get consistent colors for initials
     */
    getInitialsColors(initials: string): { backgroundColor: string; textColor: string } {
        if (!this.initialsColors.has(initials)) {
            this.initialsColors.set(initials, getInitialsColors(initials));
        }
        return this.initialsColors.get(initials);
    }
}
